import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { Box, Typography, Grid, Chip, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import HistoryIcon from '@mui/icons-material/History';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getAllLabAnalyzerRequest, removeLabAnalyzerRequest, resetRemoveLabAnalyzer } from '../../../../redux/modules/laboratory/labAnalyzer/slice';
import { useNavigate, useLocation } from 'react-router-dom';

const StatusChip = styled(Chip)(({ status }) => {
    const colors = {
        online: { bg: '#e8f5e9', color: '#2e7d32' },
        offline: { bg: '#f5f5f5', color: '#757575' },
        error: { bg: '#ffebee', color: '#c62828' },
        maintenance: { bg: '#fff3e0', color: '#e65100' },
    };
    const c = colors[status] || colors.offline;
    return { backgroundColor: c.bg, color: c.color, fontWeight: 600 };
});

const AnalyzerCard = styled(Box)(({ theme }) => ({
    padding: '20px 24px',
    borderRadius: 16,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 200ms ease',
    '&:hover': {
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.08)',
        borderColor: theme.palette.primary.main,
    },
}));

export default function LabAnalyzers() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = '/' + location.pathname.split('/')[1];
    let { getAllRes, removeRes } = useSelector(state => state.labAnalyzer);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });

    useEffect(() => {
        dispatch(setMainHeading("Analyzer Management"));
    }, [dispatch]);

    useEffect(() => {
        dispatch(getAllLabAnalyzerRequest());
    }, [dispatch]);

    useEffect(() => {
        if (removeRes?.success) {
            dispatch(resetRemoveLabAnalyzer());
            dispatch(getAllLabAnalyzerRequest());
            setDeleteDialog({ open: false, id: null, name: '' });
        }
    }, [removeRes, dispatch]);

    const handleDelete = () => {
        if (deleteDialog.id) {
            dispatch(removeLabAnalyzerRequest(deleteDialog.id));
        }
    };

    const protocolLabels = {
        ASTM_LIS2A2: 'ASTM LIS2-A2',
        HL7v2: 'HL7 v2',
        POCT1A: 'POCT1-A',
        custom: 'Custom',
    };

    const connectionLabel = (config) => {
        if (!config) return 'Not configured';
        if (config.type === 'serial') return `Serial: ${config.serialPort || '?'} @ ${config.baudRate || 9600}`;
        if (config.type === 'tcp') return `TCP: ${config.host || '?'}:${config.port || '?'}`;
        if (config.type === 'file_polling') return `File: ${config.inputDirectory || '?'}`;
        return config.type;
    };

    return (
        <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} mt={4}>
                <Typography variant="body2" color="text.secondary">
                    {getAllRes?.data?.length || 0} analyzer(s) configured
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`${basePath}/laboratory/analyzers/config`)}>
                    Add Analyzer
                </Button>
            </Stack>

            <Grid container spacing={2}>
                {getAllRes?.data?.map(analyzer => (
                    <Grid item xs={12} md={6} key={analyzer._id}>
                        <AnalyzerCard>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                        {analyzer.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {analyzer.manufacturer} {analyzer.model}
                                    </Typography>
                                </Box>
                                <StatusChip label={analyzer.status} size="small" status={analyzer.status} />
                            </Stack>

                            <Stack spacing={0.5} mb={2}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Protocol:</strong> {protocolLabels[analyzer.protocol] || analyzer.protocol}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Connection:</strong> {connectionLabel(analyzer.connectionConfig)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Department:</strong> {analyzer.departmentId?.name || 'Unassigned'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Tests Mapped:</strong> {analyzer.testMapping?.length || 0}
                                </Typography>
                                {analyzer.location && (
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Location:</strong> {analyzer.location}
                                    </Typography>
                                )}
                            </Stack>

                            <Stack direction="row" spacing={1}>
                                <IconButton size="small" title="Edit" onClick={() => navigate(`${basePath}/laboratory/analyzers/config/${analyzer._id}`)}>
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" title="Test Mapping" onClick={() => navigate(`${basePath}/laboratory/analyzers/test-mapping/${analyzer._id}`)}>
                                    <SettingsInputComponentIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" title="Communication Logs" onClick={() => navigate(`${basePath}/laboratory/analyzers/logs/${analyzer._id}`)}>
                                    <HistoryIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" title="Delete" color="error" onClick={() => setDeleteDialog({ open: true, id: analyzer._id, name: analyzer.name })}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        </AnalyzerCard>
                    </Grid>
                ))}
            </Grid>

            {(!getAllRes?.data || getAllRes.data.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography color="text.secondary">No analyzers configured. Click "Add Analyzer" to get started.</Typography>
                </Box>
            )}

            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null, name: '' })}>
                <DialogTitle>Delete Analyzer</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete <strong>{deleteDialog.name}</strong>? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, id: null, name: '' })}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
